import { FormControl, FormLabel } from '@mui/material';
import classNames from 'classnames';
import { includes } from 'lodash';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { PROCEDURE_OPTIONS } from '@/consts/selectOptions';

import { ReactComponent as ExpandMoreIcon } from '../../../../../../../../../assets/icons/patientProfileIcons/expandMoreIcon.svg';
import type { ISurgeryInfo } from '../../../../../../../../../services/intakeDataService/types';
import { dateTimeFormat } from '../../../../../../../../../services/timeService/types';
import { SELECT_NONE_OPTION } from '../../../../../../../../../utils/noneOptionUtil';
import CustomDatePicker from '../../../../../../../../CustomDatePicker';
import Select from '../../../../../../../../Inputs/Select';
import TextField from '../../../../../../../../Inputs/TextField';
import useIntakeBlockToggle, { intakeBlocksEnum } from '../../../../../hooks/useIntakeBlockToggle';
import rootStyles from '../../index.module.scss';
import styles from '../index.module.scss';
import { RemoveAction } from './SurgeryAction/RemoveAction';
import { SurgeryHeader } from './SurgeryHeader';

const SurgeryFields = ({
  surgeryIndex,
  surgery,
  onRemove,
  onUpdate,
}: {
  surgeryIndex: number;
  surgery: ISurgeryInfo;
  onRemove(index: number): void;
  onUpdate(target: HTMLInputElement, index: number): void;
}) => {
  const { control, register } = useFormContext();
  const { isRequired } = useIntakeBlockToggle();

  const surgeryOptions = [
    ...(!isRequired(intakeBlocksEnum.surgery) ? [SELECT_NONE_OPTION] : []),
    ...PROCEDURE_OPTIONS,
  ];

  const isOtherOption = () => {
    const nameFieldValue = surgery?.surgeryType ?? '';
    return (
      nameFieldValue?.toLowerCase() === 'other' ||
      (nameFieldValue &&
        !includes(
          surgeryOptions.map((option) => option.value),
          nameFieldValue,
        ))
    );
  };

  return (
    <div className="flex flex-col">
      <div className={classNames('flex items-center justify-between w-full', styles.surgeryHeader)}>
        <SurgeryHeader surgeryIndex={surgeryIndex} />
        <RemoveAction surgeryIndex={surgeryIndex} onRemove={onRemove} />
      </div>
      <div className="flex gap-4 flex-wrap">
        <FormControl
          className={classNames(rootStyles.inputWrapper, rootStyles.selectWrapper)}
          required={isRequired(intakeBlocksEnum.surgery)}
        >
          <FormLabel>Surgery type</FormLabel>
          <Controller
            name={`surgeries.${surgeryIndex}.surgeryType`}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={surgeryOptions}
                IconComponent={ExpandMoreIcon}
                classes={{
                  select: rootStyles.inputField,
                  icon: rootStyles.selectIcon,
                }}
                variant="standard"
                disableUnderline
                displayEmpty={true}
                renderValue={(value) => (
                  <div
                    className={classNames(rootStyles.selectValue, {
                      [rootStyles.placeholder]: !value || value === SELECT_NONE_OPTION.value,
                    })}
                  >
                    {value
                      ? isOtherOption()
                        ? 'other'
                        : surgeryOptions.find((option) => option?.value === value)?.label
                      : 'Select surgery type'}
                  </div>
                )}
              />
            )}
            rules={{
              required: {
                value: isRequired(intakeBlocksEnum.surgery) ?? false,
                message: 'surgery type is required',
              },
            }}
          />
        </FormControl>
        <FormControl
          required={isRequired(intakeBlocksEnum.surgery) ?? false}
          className={classNames(rootStyles.lastField, rootStyles.inputWrapper)}
        >
          <Controller
            render={({ field }) => (
              <CustomDatePicker
                label={'Surgery date'}
                {...register(`surgeries.${surgeryIndex}.surgeryDate`, {
                  required: isRequired(intakeBlocksEnum.surgery) ?? false,
                })}
                isRequired={isRequired(intakeBlocksEnum.surgery) ?? false}
                onChange={(date) => {
                  field.onChange(date?.format(dateTimeFormat.SERVER_DATE_FORMAT));
                }}
                labelClassName={styles.datePickerStyle}
              />
            )}
            name={`surgeries.${surgeryIndex}.surgeryDate`}
            control={control}
          />
        </FormControl>
      </div>
      {isOtherOption() ? (
        <TextField
          name="other"
          placeholder="Surgery name"
          wrapperClassName={rootStyles.inputWrapper}
          defaultValue={surgery?.surgeryType !== 'other' ? surgery?.surgeryType : ''}
          onBlur={(event) =>
            onUpdate(
              {
                ...(event?.target as HTMLInputElement),
                name: 'surgeryType',
              },
              surgeryIndex,
            )
          }
          inputClassName={rootStyles.inputField}
        />
      ) : null}
    </div>
  );
};
export default observer(SurgeryFields);
